import React, { useEffect, useMemo, useState } from "react";
import {
    Building2,
    Plus,
    Trash2,
    KeyRound,
    Mail,
    Users,
    FileSearch,
    Copy,
    Loader2,
    Search,
    Shield,
    Calendar as CalendarIcon,
    AlertTriangle,
    Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
    listCustomers,
    createCustomer,
    deleteCustomer,
    resetCustomerPassword,
} from "@/lib/api";

export default function Customers() {
    const { user } = useAuth();
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [showCreate, setShowCreate] = useState(false);
    const [credentialsPanel, setCredentialsPanel] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [resetting, setResetting] = useState("");

    useEffect(() => {
        document.title = "Clientes · LicitaIA";
        load();
    }, []);

    const load = async () => {
        setLoading(true);
        try {
            const d = await listCustomers();
            setCustomers(d.customers || []);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "No se pudo cargar la lista");
        } finally {
            setLoading(false);
        }
    };

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return customers;
        return customers.filter(
            (c) =>
                (c.name || "").toLowerCase().includes(q) ||
                (c.admin_email || "").toLowerCase().includes(q) ||
                (c.cif || "").toLowerCase().includes(q),
        );
    }, [customers, search]);

    const ownCompany = customers.filter((c) => c.is_owner_company);
    const realCustomers = customers.filter((c) => !c.is_owner_company);

    const totalUsers = realCustomers.reduce((s, c) => s + (c.users_count || 0), 0);
    const totalTenders = realCustomers.reduce((s, c) => s + (c.tenders_count || 0), 0);

    const onResetPassword = async (companyId) => {
        setResetting(companyId);
        try {
            const r = await resetCustomerPassword(companyId);
            setCredentialsPanel({
                email: r.admin_email,
                password: r.new_password,
                mode: "reset",
            });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "No se pudo regenerar la contraseña");
        } finally {
            setResetting("");
        }
    };

    if (!user?.is_admin) {
        return (
            <div className="p-8" data-testid="customers-blocked">
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 max-w-xl">
                    <div className="flex items-center gap-2 text-amber-700 font-bold mb-2">
                        <Shield size={18} />
                        Acceso restringido
                    </div>
                    <p className="text-sm text-amber-800">
                        Esta sección es para la administración del SaaS. Habla con la dueña
                        de la plataforma si necesitas acceso.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 md:p-8 space-y-6" data-testid="customers-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div>
                    <div className="text-xs font-bold uppercase tracking-[0.18em] text-brand-600 mb-1">
                        Administración del SaaS
                    </div>
                    <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight text-ink-950">
                        Clientes
                    </h1>
                    <p className="text-ink-500 text-sm mt-1 max-w-2xl">
                        Cada cliente es una empresa aislada con sus propios usuarios, tenders
                        y documentos. No comparten datos entre sí ni contigo.
                    </p>
                </div>
                <Button
                    onClick={() => setShowCreate(true)}
                    className="rounded-full bg-brand-600 hover:bg-brand-700 text-white px-6 h-11 font-bold shadow-pop"
                    data-testid="create-customer-btn"
                >
                    <Plus size={16} className="mr-2" />
                    Nuevo cliente
                </Button>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Kpi
                    icon={Building2}
                    label="Clientes activos"
                    value={realCustomers.length}
                    color="from-brand-600 to-cyan-500"
                />
                <Kpi
                    icon={Users}
                    label="Usuarios de cliente"
                    value={totalUsers}
                    color="from-cyan-500 to-sky-400"
                />
                <Kpi
                    icon={FileSearch}
                    label="Licitaciones cliente"
                    value={totalTenders}
                    color="from-sky-400 to-brand-500"
                />
                <Kpi
                    icon={Shield}
                    label="Tu empresa interna"
                    value={ownCompany.length}
                    color="from-brand-500 to-brand-700"
                />
            </div>

            {/* Search */}
            <div className="relative max-w-md">
                <Search
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400"
                />
                <Input
                    placeholder="Buscar por nombre, email o CIF…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 rounded-xl h-11"
                    data-testid="customers-search"
                />
            </div>

            {/* Customers table */}
            <div className="bg-white rounded-3xl border border-ink-100 shadow-pop overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-ink-400">
                        <Loader2 size={28} className="animate-spin mx-auto mb-3" />
                        Cargando clientes…
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="p-12 text-center">
                        <Building2 size={36} className="mx-auto text-ink-300 mb-3" />
                        <div className="font-display font-bold text-ink-700">
                            Aún no hay clientes
                        </div>
                        <p className="text-sm text-ink-500 mt-1 mb-5">
                            Cuando crees un cliente, le abrimos una empresa privada y le damos
                            credenciales para entrar.
                        </p>
                        <Button
                            onClick={() => setShowCreate(true)}
                            className="rounded-full bg-brand-600 hover:bg-brand-700 text-white px-6 h-11 font-bold"
                            data-testid="empty-create-customer-btn"
                        >
                            <Plus size={16} className="mr-2" />
                            Crear el primero
                        </Button>
                    </div>
                ) : (
                    <table className="w-full text-sm" data-testid="customers-table">
                        <thead className="bg-ink-50/60 border-b border-ink-100">
                            <tr className="text-left text-[11px] font-bold uppercase tracking-[0.14em] text-ink-500">
                                <th className="px-5 py-3">Empresa</th>
                                <th className="px-5 py-3">Admin · Email</th>
                                <th className="px-5 py-3">CIF</th>
                                <th className="px-5 py-3 text-center">Usuarios</th>
                                <th className="px-5 py-3 text-center">Licitaciones</th>
                                <th className="px-5 py-3">Alta</th>
                                <th className="px-5 py-3 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-ink-100">
                            {filtered.map((c) => (
                                <tr
                                    key={c.id}
                                    className="hover:bg-brand-50/30 transition-colors"
                                    data-testid={`customer-row-${c.id}`}
                                >
                                    <td className="px-5 py-4">
                                        <div className="flex items-center gap-3">
                                            <div
                                                className={`w-9 h-9 rounded-xl flex items-center justify-center text-white shadow-pop ${
                                                    c.is_owner_company
                                                        ? "bg-gradient-to-br from-brand-700 to-brand-900"
                                                        : "bg-gradient-to-br from-brand-600 to-cyan-500"
                                                }`}
                                            >
                                                <Building2 size={15} />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="font-bold text-ink-950 truncate max-w-[220px]">
                                                    {c.name}
                                                </div>
                                                {c.is_owner_company && (
                                                    <div className="text-[10px] uppercase tracking-wider font-bold text-brand-700 mt-0.5">
                                                        Empresa interna
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-5 py-4 text-ink-700">
                                        {c.admin_email || (
                                            <span className="text-ink-400 italic text-xs">
                                                sin admin
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-5 py-4 font-mono text-xs text-ink-600">
                                        {c.cif || "—"}
                                    </td>
                                    <td className="px-5 py-4 text-center font-mono font-bold">
                                        {c.users_count}
                                    </td>
                                    <td className="px-5 py-4 text-center font-mono font-bold text-brand-700">
                                        {c.tenders_count}
                                    </td>
                                    <td className="px-5 py-4 text-xs text-ink-500">
                                        {formatDate(c.created_at)}
                                    </td>
                                    <td className="px-5 py-4">
                                        <div className="flex items-center justify-end gap-1.5">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="h-8 rounded-lg text-xs font-bold border-ink-200"
                                                disabled={
                                                    !c.admin_user_id || resetting === c.id
                                                }
                                                onClick={() => onResetPassword(c.id)}
                                                data-testid={`reset-pw-${c.id}`}
                                            >
                                                {resetting === c.id ? (
                                                    <Loader2
                                                        size={13}
                                                        className="animate-spin mr-1"
                                                    />
                                                ) : (
                                                    <KeyRound size={13} className="mr-1" />
                                                )}
                                                Reset
                                            </Button>
                                            {!c.is_owner_company && (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="h-8 rounded-lg text-xs font-bold border-red-200 text-red-700 hover:bg-red-50"
                                                    onClick={() => setConfirmDelete(c)}
                                                    data-testid={`delete-customer-${c.id}`}
                                                >
                                                    <Trash2 size={13} />
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Create dialog */}
            {showCreate && (
                <CreateCustomerDialog
                    onClose={() => setShowCreate(false)}
                    onCreated={(creds) => {
                        setShowCreate(false);
                        setCredentialsPanel({ ...creds, mode: "create" });
                        load();
                    }}
                />
            )}

            {/* Credentials panel (shown right after create or password reset) */}
            {credentialsPanel && (
                <CredentialsDialog
                    data={credentialsPanel}
                    onClose={() => setCredentialsPanel(null)}
                />
            )}

            {/* Confirm delete */}
            {confirmDelete && (
                <DeleteCustomerDialog
                    customer={confirmDelete}
                    onClose={() => setConfirmDelete(null)}
                    onDeleted={() => {
                        setConfirmDelete(null);
                        load();
                    }}
                />
            )}
        </div>
    );
}

function Kpi({ icon: Icon, label, value, color }) {
    return (
        <div className="bg-white rounded-2xl p-4 border border-ink-100 shadow-pop">
            <div
                className={`w-10 h-10 rounded-xl bg-gradient-to-br ${color} text-white flex items-center justify-center shadow-pop mb-3`}
            >
                <Icon size={18} />
            </div>
            <div className="font-display text-2xl font-extrabold text-ink-950 leading-none">
                {value}
            </div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-ink-400 font-bold mt-1">
                {label}
            </div>
        </div>
    );
}

function CreateCustomerDialog({ onClose, onCreated }) {
    const [form, setForm] = useState({
        company_name: "",
        cif: "",
        admin_name: "",
        admin_email: "",
        admin_password: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const generatePassword = () => {
        const chars =
            "ABCDEFGHJKLMNPQRSTUVWXYZ23456789abcdefghijkmnpqrstuvwxyz!#$%&*?";
        const len = 14;
        let pw = "";
        for (let i = 0; i < len; i++)
            pw += chars[Math.floor(Math.random() * chars.length)];
        setForm({ ...form, admin_password: pw });
    };

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        if (
            !form.company_name.trim() ||
            !form.admin_email.trim() ||
            !form.admin_password
        ) {
            setError("Nombre de empresa, email y contraseña son obligatorios.");
            return;
        }
        setSubmitting(true);
        try {
            const r = await createCustomer(form);
            toast.success("Cliente creado correctamente");
            onCreated({
                email: r.admin?.email,
                password: form.admin_password,
                company: r.company?.name,
            });
        } catch (err) {
            const msg =
                err?.response?.data?.detail ||
                "No se pudo crear el cliente. Revisa los datos.";
            setError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                className="max-w-2xl rounded-3xl"
                data-testid="create-customer-dialog"
            >
                <DialogHeader>
                    <div className="flex items-center gap-3 mb-1">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-600 to-cyan-500 text-white flex items-center justify-center shadow-pop">
                            <Building2 size={18} />
                        </div>
                        <div>
                            <DialogTitle className="font-display text-2xl font-black">
                                Nuevo cliente
                            </DialogTitle>
                            <DialogDescription className="text-sm">
                                Se creará una empresa aislada con un administrador inicial.
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <form onSubmit={submit} className="space-y-5 mt-2">
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 mb-3">
                            Datos de la empresa
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div className="sm:col-span-2">
                                <Label>Nombre de la empresa *</Label>
                                <Input
                                    value={form.company_name}
                                    onChange={update("company_name")}
                                    placeholder="Construcciones Ejemplo SL"
                                    data-testid="new-customer-company-name"
                                />
                            </div>
                            <div>
                                <Label>CIF</Label>
                                <Input
                                    value={form.cif}
                                    onChange={update("cif")}
                                    placeholder="B12345678"
                                    data-testid="new-customer-cif"
                                    className="font-mono"
                                />
                            </div>
                        </div>
                    </div>

                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 mb-3">
                            Administrador inicial
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <Label>Nombre del administrador</Label>
                                <Input
                                    value={form.admin_name}
                                    onChange={update("admin_name")}
                                    placeholder="María García"
                                    data-testid="new-customer-admin-name"
                                />
                            </div>
                            <div>
                                <Label>Email *</Label>
                                <Input
                                    type="email"
                                    value={form.admin_email}
                                    onChange={update("admin_email")}
                                    placeholder="director@empresa.es"
                                    data-testid="new-customer-admin-email"
                                />
                            </div>
                            <div className="sm:col-span-2">
                                <div className="flex items-center justify-between mb-1">
                                    <Label>Contraseña inicial *</Label>
                                    <button
                                        type="button"
                                        onClick={generatePassword}
                                        className="text-[11px] font-bold uppercase tracking-wider text-brand-600 hover:text-brand-700"
                                        data-testid="generate-password-btn"
                                    >
                                        ✨ Generar segura
                                    </button>
                                </div>
                                <Input
                                    value={form.admin_password}
                                    onChange={update("admin_password")}
                                    placeholder="Mín. 10 caracteres con mayús, minús, número y símbolo"
                                    className="font-mono"
                                    data-testid="new-customer-password"
                                />
                                <div className="text-xs text-ink-400 mt-1">
                                    Te la enseñaremos una vez tras crear el cliente. Cópiala y
                                    envíasela.
                                </div>
                            </div>
                        </div>
                    </div>

                    {error && (
                        <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-800 p-3 rounded-xl text-sm">
                            <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}

                    <DialogFooter className="gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            className="rounded-full"
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            disabled={submitting}
                            className="rounded-full bg-brand-600 hover:bg-brand-700 text-white font-bold"
                            data-testid="submit-new-customer"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 size={15} className="animate-spin mr-2" />
                                    Creando…
                                </>
                            ) : (
                                <>
                                    <Plus size={15} className="mr-2" />
                                    Crear cliente
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function CredentialsDialog({ data, onClose }) {
    const [copied, setCopied] = useState("");
    const copy = (text, key) => {
        navigator.clipboard.writeText(text);
        setCopied(key);
        toast.success("Copiado al portapapeles");
        setTimeout(() => setCopied(""), 1500);
    };
    const title =
        data.mode === "reset"
            ? "Nueva contraseña generada"
            : "Cliente creado · credenciales";

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                className="max-w-xl rounded-3xl"
                data-testid="credentials-dialog"
            >
                <DialogHeader>
                    <div className="flex items-center gap-3 mb-1">
                        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500 text-white flex items-center justify-center shadow-pop">
                            <Check size={20} />
                        </div>
                        <div>
                            <DialogTitle className="font-display text-2xl font-black">
                                {title}
                            </DialogTitle>
                            {data.company && (
                                <DialogDescription className="text-sm">
                                    Empresa: <strong>{data.company}</strong>
                                </DialogDescription>
                            )}
                        </div>
                    </div>
                </DialogHeader>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800 flex items-start gap-2 mb-4">
                    <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                    <span>
                        <strong>Cópialas ahora</strong> — esta contraseña no se vuelve a mostrar.
                        Compártelas con tu cliente por un canal seguro.
                    </span>
                </div>

                <div className="space-y-3">
                    <CredentialRow
                        icon={Mail}
                        label="Email"
                        value={data.email}
                        copied={copied === "email"}
                        onCopy={() => copy(data.email, "email")}
                    />
                    <CredentialRow
                        icon={KeyRound}
                        label="Contraseña"
                        value={data.password}
                        mono
                        copied={copied === "pw"}
                        onCopy={() => copy(data.password, "pw")}
                    />
                </div>

                <DialogFooter className="mt-5">
                    <Button
                        onClick={onClose}
                        className="rounded-full bg-brand-600 hover:bg-brand-700 text-white"
                        data-testid="close-credentials"
                    >
                        Entendido, hecho
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function CredentialRow({ icon: Icon, label, value, mono = false, copied, onCopy }) {
    return (
        <div className="bg-white border border-ink-100 rounded-xl p-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center shrink-0">
                    <Icon size={15} />
                </div>
                <div className="min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-ink-400">
                        {label}
                    </div>
                    <div
                        className={`text-ink-950 font-semibold ${
                            mono ? "font-mono text-sm" : "text-sm"
                        } truncate`}
                    >
                        {value}
                    </div>
                </div>
            </div>
            <Button
                size="sm"
                variant="outline"
                onClick={onCopy}
                className="rounded-lg h-9 text-xs font-bold border-ink-200 shrink-0"
            >
                {copied ? (
                    <Check size={13} className="text-emerald-600" />
                ) : (
                    <Copy size={13} />
                )}
            </Button>
        </div>
    );
}

function DeleteCustomerDialog({ customer, onClose, onDeleted }) {
    const [purgeData, setPurgeData] = useState(false);
    const [confirmText, setConfirmText] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const canDelete = confirmText.trim().toLowerCase() === (customer.name || "").toLowerCase();

    const submit = async () => {
        setSubmitting(true);
        try {
            const r = await deleteCustomer(customer.id, purgeData);
            toast.success(
                `Cliente eliminado · ${r.users_deleted} usuarios borrados${
                    purgeData
                        ? ` · ${Object.values(r.docs_deleted || {}).reduce(
                              (a, b) => a + b,
                              0,
                          )} documentos purgados`
                        : ""
                }`,
            );
            onDeleted();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error al eliminar");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                className="max-w-lg rounded-3xl"
                data-testid="delete-customer-dialog"
            >
                <DialogHeader>
                    <div className="flex items-center gap-3 mb-1">
                        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-red-500 to-red-700 text-white flex items-center justify-center shadow-pop">
                            <Trash2 size={18} />
                        </div>
                        <div>
                            <DialogTitle className="font-display text-2xl font-black">
                                Eliminar cliente
                            </DialogTitle>
                            <DialogDescription className="text-sm">
                                Esta acción no se puede deshacer.
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <div className="space-y-4 mt-2">
                    <div className="bg-ink-50 rounded-xl p-3 text-sm">
                        <div className="font-bold text-ink-950">{customer.name}</div>
                        <div className="text-ink-500">
                            {customer.admin_email} · {customer.users_count} usuarios ·{" "}
                            {customer.tenders_count} licitaciones
                        </div>
                    </div>

                    <label className="flex items-start gap-3 cursor-pointer bg-red-50 border border-red-200 rounded-xl p-3">
                        <input
                            type="checkbox"
                            checked={purgeData}
                            onChange={(e) => setPurgeData(e.target.checked)}
                            className="mt-1"
                            data-testid="purge-data-checkbox"
                        />
                        <div className="text-sm text-red-900">
                            <div className="font-bold">Borrar también todos los datos</div>
                            <div className="text-xs text-red-700 mt-0.5">
                                Marca solo si el cliente se va y no debe quedar rastro de sus
                                tenders, documentos, comunicaciones y solvencia.
                            </div>
                        </div>
                    </label>

                    <div>
                        <Label>
                            Escribe <span className="font-mono">{customer.name}</span> para
                            confirmar
                        </Label>
                        <Input
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            placeholder={customer.name}
                            data-testid="delete-confirm-text"
                            className="font-mono"
                        />
                    </div>
                </div>

                <DialogFooter className="gap-2 mt-2">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onClose}
                        className="rounded-full"
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={!canDelete || submitting}
                        className="rounded-full bg-red-600 hover:bg-red-700 text-white font-bold disabled:opacity-50"
                        data-testid="confirm-delete-customer"
                    >
                        {submitting ? (
                            <>
                                <Loader2 size={14} className="animate-spin mr-2" />
                                Eliminando…
                            </>
                        ) : (
                            <>
                                <Trash2 size={14} className="mr-2" />
                                Eliminar definitivamente
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Label({ children }) {
    return (
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 mb-1.5">
            {children}
        </div>
    );
}

function formatDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("es-ES", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return "—";
    }
}
